import { CircleSlash, Loader2, MessageSquare, PhoneForwarded, CalendarCheck } from 'lucide-react';
import type { WorkflowTestResult, WorkflowTestResultAction } from '@vagusx/clients';
import { Badge } from '@/components/ui/badge';
import { useWorkflowTestResult } from '@/hooks/use-workflows';
import { formatFactValue } from './test-call';

/**
 * What a finished test call understood, and what it would have done (ADR-306).
 *
 * One block per step that ran, in the order the flow lists them: how it ended,
 * the facts it wrote — labelled by the component that wrote them — and every
 * write the test withheld instead of making: the booking, the move, the
 * cancellation, the transfer, the text (with the exact words a patient would
 * have received). Nothing listed here happened.
 */

type Report = NonNullable<WorkflowTestResult['report']>;

const ACTION_TITLE: Record<WorkflowTestResultAction['kind'], string> = {
  book: 'Would book',
  reschedule: 'Would move the appointment',
  cancel: 'Would cancel the appointment',
  send_sms: 'Would send a text',
  transfer: 'Would put them through to a person',
};

function ActionIcon({ kind }: { kind: WorkflowTestResultAction['kind'] }): React.ReactElement {
  if (kind === 'send_sms') return <MessageSquare className="h-4 w-4" />;
  if (kind === 'transfer') return <PhoneForwarded className="h-4 w-4" />;
  if (kind === 'cancel') return <CircleSlash className="h-4 w-4" />;
  return <CalendarCheck className="h-4 w-4" />;
}

function Action({ action }: { action: WorkflowTestResultAction }): React.ReactElement {
  return (
    <div className="rounded-md border border-dashed p-3">
      <div className="flex items-center gap-2 text-sm font-medium">
        <ActionIcon kind={action.kind} />
        {ACTION_TITLE[action.kind]}
        <Badge variant="outline" className="ml-auto">
          not done: test
        </Badge>
      </div>
      <dl className="mt-2 grid grid-cols-3 gap-x-3 gap-y-1 text-xs">
        {action.details.map((d) => (
          <div key={d.label} className="contents">
            <dt className="text-muted-foreground">{d.label}</dt>
            <dd className="col-span-2">{formatFactValue(d.value)}</dd>
          </div>
        ))}
      </dl>
      {action.message && (
        <pre className="mt-2 whitespace-pre-wrap rounded bg-muted p-2 font-sans text-xs">
          {action.message}
        </pre>
      )}
    </div>
  );
}

export function TestReportView({ report }: { report: Report }): React.ReactElement {
  return (
    <div className="flex flex-col gap-4">
      {report.steps.map((step) => (
        <section key={step.node} className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold">{step.name}</h3>
            {step.plan === 'stub' && <Badge variant="secondary">stood in</Badge>}
            {step.plan === 'off' && <Badge variant="secondary">turned off</Badge>}
            <span className="ml-auto text-xs text-muted-foreground">
              {step.outcome ?? 'did not finish'}
            </span>
          </div>
          {step.facts.length > 0 && (
            <dl className="grid grid-cols-3 gap-x-3 gap-y-1 text-xs">
              {step.facts.map((f) => (
                <div key={f.fact} className="contents">
                  <dt className="text-muted-foreground">{f.label}</dt>
                  <dd className="col-span-2">{formatFactValue(f.value)}</dd>
                </div>
              ))}
            </dl>
          )}
          {step.actions.map((a, i) => (
            <Action key={i} action={a} />
          ))}
        </section>
      ))}
      {report.unattributed.length > 0 && (
        <section className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold">Outside any step</h3>
          {report.unattributed.map((a, i) => (
            <Action key={i} action={a} />
          ))}
        </section>
      )}
    </div>
  );
}

/** The report for one finished test call, asked for until the worker has posted it. */
export function TestReportPanel({
  workflowId,
  interactionId,
}: {
  workflowId: string;
  interactionId: string;
}): React.ReactElement {
  const { data, isError, gaveUp } = useWorkflowTestResult(workflowId, interactionId);
  const report = data?.report ?? null;
  return (
    <div className="border-t p-4">
      <h2 className="mb-3 text-sm font-semibold">What the test understood</h2>
      {report ? (
        <TestReportView report={report} />
      ) : isError ? (
        <p className="text-sm text-muted-foreground">The report could not be loaded.</p>
      ) : gaveUp ? (
        <p className="text-sm text-muted-foreground">
          No report arrived. The transcript in the post-call view still shows the call.
        </p>
      ) : (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Waiting for the call to report…
        </p>
      )}
    </div>
  );
}
